const LOCK_TTL_MS = 30000;
const WAIT_TIMEOUT_MS = 20000;
const POLL_MS = 50;
/** Refresh this many ms before access-token expiry to avoid 401 stampedes. */
const REFRESH_SKEW_MS = 60_000;
const MIN_SCHEDULE_DELAY_MS = 5_000;

const bearerToken = (value) =>
  String(value || "").replace(/^Bearer\s+/i, "").trim();

const headerValue = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase());
  }
  return headers[name] || headers[name.toLowerCase()];
};

export const getFailedAccessToken = (request) =>
  bearerToken(headerValue(request?.headers, "Authorization"));

export const applyAccessToken = (request, access) => {
  if (!request || !access) return request;
  request.headers = request.headers || {};
  if (typeof request.headers.set === "function") {
    request.headers.set("Authorization", "Bearer " + access);
  } else {
    request.headers.Authorization = "Bearer " + access;
  }
  return request;
};

export const withoutAuthHeader = (config) => {
  if (!config?.headers) return config;
  if (typeof config.headers.delete === "function") {
    config.headers.delete("Authorization");
    config.headers.delete("authorization");
  } else {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
};

/** Decode JWT `exp` (ms) without verifying the signature. */
export const getJwtExpiryMs = (token) => {
  try {
    const segment = String(token || "").split(".")[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * True when the refresh failure means the session is actually dead
 * (invalid/blacklisted refresh). Transient 429/5xx/network must not log out.
 */
export const shouldEndSessionOnRefreshError = (error, hasRefreshToken) => {
  if (!hasRefreshToken) return true;
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Single-flight token refresh, including a short localStorage lock so two tabs
 * do not rotate the same refresh token at once.
 */
export function createTokenRefresher({
  lockKey,
  getAccessToken,
  getRefreshToken,
  setToken,
  requestRefresh
}) {
  const tabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let inFlight = null;
  let scheduleTimer = null;

  const readTokens = () => ({
    access: getAccessToken(),
    refresh: getRefreshToken()
  });

  const hasNewerAccess = (failedAccessToken) => {
    const current = getAccessToken();
    return Boolean(failedAccessToken && current && current !== failedAccessToken);
  };

  const hasRotatedRefresh = (previousRefresh) => {
    const current = readTokens();
    return Boolean(
      current.access && current.refresh && current.refresh !== previousRefresh
    );
  };

  const readLock = () => {
    try {
      return JSON.parse(localStorage.getItem(lockKey) || "null");
    } catch {
      return null;
    }
  };

  const acquireLock = () => {
    const now = Date.now();
    const existing = readLock();
    if (
      existing?.id &&
      existing.id !== tabId &&
      now - existing.ts < LOCK_TTL_MS
    ) {
      return false;
    }
    localStorage.setItem(lockKey, JSON.stringify({ id: tabId, ts: now }));
    const stored = readLock();
    return stored?.id === tabId;
  };

  const releaseLock = () => {
    const existing = readLock();
    if (!existing || existing.id === tabId) {
      localStorage.removeItem(lockKey);
    }
  };

  const waitForRotation = (previousRefresh) =>
    new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        clearTimeout(timeoutId);
        clearInterval(pollId);
        window.removeEventListener("storage", onStorage);
      };

      const finish = (cb) => {
        if (settled) return;
        settled = true;
        cleanup();
        cb();
      };

      const onStorage = () => {
        if (hasRotatedRefresh(previousRefresh)) {
          finish(() => resolve(readTokens()));
        }
      };

      const timeoutId = setTimeout(() => {
        finish(() =>
          reject(new Error("Timed out waiting for token refresh"))
        );
      }, WAIT_TIMEOUT_MS);
      const pollId = setInterval(onStorage, POLL_MS);
      window.addEventListener("storage", onStorage);
      onStorage();
    });

  const refresh = async (failedAccessToken) => {
    if (hasNewerAccess(failedAccessToken)) {
      return readTokens();
    }

    const previousRefresh = getRefreshToken();
    if (!previousRefresh) {
      throw new Error("No refresh token");
    }

    if (!acquireLock()) {
      try {
        return await waitForRotation(previousRefresh);
      } catch (error) {
        if (hasNewerAccess(failedAccessToken) || hasRotatedRefresh(previousRefresh)) {
          return readTokens();
        }
        if (!acquireLock()) {
          throw error;
        }
      }
    }

    try {
      if (hasNewerAccess(failedAccessToken) || hasRotatedRefresh(previousRefresh)) {
        return readTokens();
      }
      const data = await requestRefresh(previousRefresh);
      setToken(data);
      return {
        access: data.access,
        refresh: data.refresh || getRefreshToken()
      };
    } catch (error) {
      if (hasNewerAccess(failedAccessToken) || hasRotatedRefresh(previousRefresh)) {
        return readTokens();
      }
      throw error;
    } finally {
      releaseLock();
    }
  };

  const runRefresh = (failedAccessToken) => {
    if (!inFlight) {
      inFlight = refresh(failedAccessToken)
        .then((tokens) => {
          scheduleProactiveRefresh();
          return tokens;
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };

  const scheduleProactiveRefresh = () => {
    if (typeof window === "undefined") return;
    clearTimeout(scheduleTimer);
    const access = getAccessToken();
    if (!access || !getRefreshToken()) return;

    const expMs = getJwtExpiryMs(access);
    if (!expMs) return;

    const delay = Math.max(expMs - Date.now() - REFRESH_SKEW_MS, MIN_SCHEDULE_DELAY_MS);
    scheduleTimer = setTimeout(() => {
      const current = getAccessToken();
      if (!current || !getRefreshToken()) return;
      runRefresh(current).catch(() => {
        // Leave failures to the 401 interceptor; do not clear the session here.
      });
    }, delay);
  };

  scheduleProactiveRefresh();

  runRefresh.scheduleProactiveRefresh = scheduleProactiveRefresh;
  return runRefresh;
}
